import React, { lazy, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import logger from '../../utils/logger';
import { formatCurrency } from '../../utils/formatters';
import { centsToEuros } from '../../utils/moneyUtils';
import { Amount } from '../common/Amount';
import { ChartCard, type ChartLoadState } from './ChartCard';
import { computeDso } from '../../utils/cockpitReceivables';
import { computeNettoCash, type NettoCashResult, type NettoCashWeek } from '../../utils/cockpitNettoCash';
import { loadOpenItemRows, type CockpitOpenItemRow } from '../../loaders/cockpitLoaders';
import type { Expense } from '../../types/expenseTypes';

/**
 * `cockpit/NettoCashTile` (Design-Identität v5 „Kontor",
 * `architecture-ux-design-v5.md` §6.3, Arbeitspaket M-D5-C2).
 *
 * ERSETZT `LiquidityForecastTile` in der Charts-Zone. Zeigt die echte
 * Netto-Cash-Vorschau (erwartete Eingänge − bekannte Ausgänge je Woche +
 * kumulierte Netto-Linie) statt einer nach Datum gebucketeten Kopie des
 * Aging-Charts. Das Modell (`utils/cockpitNettoCash#computeNettoCash`) wird aus
 * den bereits gebubbelten Rohdaten der Zahlen-Kacheln komponiert — 0 zusätzliche
 * Firestore-Reads (offene Posten via `rows`, Ausgaben via `expenses`, DSO pure
 * aus `rows`, USt-Zahllast + Termin als Props von der Seite).
 *
 * Fail-closed (§6.3): ist keine USt-Zahllast berechenbar (`vatReturn===null`),
 * zeigt die Kachel den „ohne USt-Abfluss"-Hinweis (keine stille Halb-Wahrheit).
 * Kachel-Isolation (Muster `LiquidityForecastTile`/`ResultYtdTile`): eigener
 * isolierter Retry (lädt die offenen Posten neu); ein Fehler crasht nie die
 * Seite oder Geschwister-Kacheln.
 *
 * Wording ist DE-maßgeblich (Titel/Untertitel/Abgrenzungssatz — Jurist-
 * unkritisch, da KEINE Rechtsbegriffe, trotzdem ehrlich: NICHT „Kontostand").
 *
 * @module components/cockpit/NettoCashTile
 */

const NettoCashChart = lazy(() => import('./NettoCashChart'));

/** `prefers-reduced-motion` — bewusst dupliziert (Muster `LiquidityForecastTile`). */
function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/** Today's date as ISO YYYY-MM-DD (local timezone) — bewusst dupliziert. */
function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Kurzes „TT.MM."-Label des Wochenbeginns (UTC, kein DST-Drift); leer/ungültig ⇒ Pass-through. */
function formatWeekLabel(weekStartISO: string, locale: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(weekStartISO)) return weekStartISO;
  return new Date(`${weekStartISO}T00:00:00Z`).toLocaleDateString(locale, {
    day: '2-digit',
    month: '2-digit',
    timeZone: 'UTC',
  });
}

/** Alle Wochen ohne Bewegung (kein Ein-/Ausfluss) ⇒ positiver Zero-State statt leerem Chart. */
function isAllZero(weeks: NettoCashWeek[]): boolean {
  return weeks.every((w) => w.inflowCents === 0 && w.outflowCents === 0);
}

export interface NettoCashTileProps {
  /** Ziel-Uid für den isolierten Retry-Reload (i. d. R. `effectiveScopeUid`). */
  scopeUid: string;
  /** Bereits gebubbelte offene-Posten-Rohdaten (EIN-Seite); `null` = noch nicht geladen. */
  rows: CockpitOpenItemRow[] | null;
  /** Ladefehler der offenen Posten. */
  rowsError: boolean;
  /** Bereits gebubbelte Ausgaben (AUS(1)-Seite); `null` = noch nicht geladen. */
  expenses: Expense[] | null;
  /** Ladefehler der Ausgaben. */
  expensesError: boolean;
  /** `EXPENSES_ENABLED` — `false` ⇒ Ausgaben werden als leer behandelt (kein Warten). */
  expensesEnabled: boolean;
  /** USt-Zahllast `{ payableCents }` (Soll-Pfad) oder `null` (fail-closed ⇒ „ohne USt-Abfluss"). */
  vatReturn: { payableCents: number } | null;
  /** `true`, solange die USt-Zahllast noch geladen wird ⇒ Kachel bleibt im Ladezustand. */
  vatPending: boolean;
  /** Nächster UStVA-Termin (ISO) — die Woche des USt-Abflusses; `null` ⇒ kein Termin. */
  ustvaDeadlineISO: string | null;
  /** Horizont in Wochen (Default via `computeNettoCash` = 8). */
  horizonWeeks?: number;
}

const NettoCashTile: React.FC<NettoCashTileProps> = ({
  scopeUid,
  rows,
  rowsError,
  expenses,
  expensesError,
  expensesEnabled,
  vatReturn,
  vatPending,
  ustvaDeadlineISO,
  horizonWeeks,
}) => {
  const { t, i18n } = useTranslation('cockpit');
  const navigate = useNavigate();
  const [manualRows, setManualRows] = useState<ChartLoadState<CockpitOpenItemRow[]> | null>(null);

  const today = todayISO();

  // Zeilen-Ladezustand (mit isoliertem Retry-Override). Muster `LiquidityForecastTile`.
  const rowsState: ChartLoadState<CockpitOpenItemRow[]> =
    manualRows ?? (rowsError ? { status: 'error' } : rows !== null ? { status: 'ready', data: rows } : { status: 'loading' });

  // Ausgaben gelten als „bereit", wenn das Feature aus ist (dann leere Menge).
  const expensesReady = !expensesEnabled || expenses !== null;

  // Kombinierter Zustand: Fehler (Zeilen/Ausgaben) dominiert; sonst Laden bis
  // ALLE drei Quellen (Zeilen, Ausgaben, USt-Zahllast) verfügbar sind.
  const derivedState: ChartLoadState<CockpitOpenItemRow[]> =
    rowsState.status === 'error' || expensesError
      ? { status: 'error' }
      : rowsState.status === 'ready' && expensesReady && !vatPending
        ? rowsState
        : { status: 'loading' };

  const retry = async () => {
    setManualRows({ status: 'loading' });
    try {
      const freshRows = await loadOpenItemRows(scopeUid);
      setManualRows({ status: 'ready', data: freshRows });
    } catch (err) {
      setManualRows({ status: 'error' });
      logger.error('NettoCashTile: Chart-Retry (Netto-Cash-Vorschau) fehlgeschlagen', err);
    }
  };

  /** Komponiert das Netto-Cash-Modell aus den geladenen Zeilen + gebubbelten Props. */
  const buildModel = (data: CockpitOpenItemRow[]): NettoCashResult =>
    computeNettoCash({
      openItems: data,
      dso: computeDso(data, today),
      expenses: expenses ?? [],
      vatReturn,
      ustvaDeadlineISO,
      todayISO: today,
      horizonWeeks,
    });

  const inflowLabel = t('charts.nettoCash.seriesInflow');
  const outflowLabel = t('charts.nettoCash.seriesOutflow');
  const netLabel = t('charts.nettoCash.seriesNet');
  const vatLabel = t('charts.nettoCash.seriesVat');

  // Untertitel + Abgrenzungssatz (+ ggf. „ohne USt-Abfluss"-Hinweis), in ALLEN
  // Zuständen sichtbar (§6.3): ehrliches Labeling, NICHT „Kontostand".
  const subtitle = (
    <>
      <p className="text-xs text-muted">{t('charts.nettoCash.subtitle')}</p>
      <p className="text-xs text-muted">{t('charts.nettoCash.distinction')}</p>
      {vatReturn === null && !vatPending && (
        <p className="text-xs text-muted">{t('charts.nettoCash.vatHint')}</p>
      )}
    </>
  );

  return (
    <ChartCard<CockpitOpenItemRow[]>
      title={t('charts.nettoCash.title')}
      subtitle={subtitle}
      state={derivedState}
      onRetry={retry}
      onClick={() => navigate('/dashboard/offene-posten')}
      toggleToTableLabel={t('charts.toggleToTable')}
      toggleToChartLabel={t('charts.toggleToChart')}
      emptyDashLabel={t('empty.dash')}
      retryLabel={t('retry')}
      ariaLabel={(data) => {
        const model = buildModel(data);
        if (isAllZero(model.weeks)) return t('charts.nettoCash.emptyHint');
        const entries = model.weeks.map(
          (w) =>
            `${formatWeekLabel(w.weekStartISO, i18n.language)}: ${inflowLabel} ${formatCurrency(centsToEuros(w.inflowCents))}, ${outflowLabel} ${formatCurrency(centsToEuros(w.outflowCents))}, ${netLabel} ${formatCurrency(centsToEuros(w.cumulativeNetCents))}.`,
        );
        return `${t('charts.nettoCash.ariaLabelIntro')} ${entries.join(' ')}`;
      }}
      renderChart={(data) => {
        const model = buildModel(data);
        if (isAllZero(model.weeks)) {
          return <p className="text-sm text-secondary py-4 text-center">{t('charts.nettoCash.emptyHint')}</p>;
        }
        return (
          <NettoCashChart
            weeks={model.weeks}
            weekLabel={(iso) => formatWeekLabel(iso, i18n.language)}
            inflowLabel={inflowLabel}
            outflowLabel={outflowLabel}
            netLabel={netLabel}
            vatLabel={vatLabel}
            reducedMotion={prefersReducedMotion()}
            formatCurrency={(cents) => formatCurrency(centsToEuros(cents))}
            locale={i18n.language}
          />
        );
      }}
      renderTable={(data) => {
        const model = buildModel(data);
        if (isAllZero(model.weeks)) {
          return <p className="text-sm text-secondary py-4 text-center">{t('charts.nettoCash.emptyHint')}</p>;
        }
        return (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-secondary">
                  <th className="py-1 pr-3 font-medium">{t('charts.nettoCash.tableColumnWeek')}</th>
                  <th className="py-1 pr-3 font-medium text-right">{t('charts.nettoCash.tableColumnInflow')}</th>
                  <th className="py-1 pr-3 font-medium text-right">{t('charts.nettoCash.tableColumnOutflow')}</th>
                  <th className="py-1 font-medium text-right">{t('charts.nettoCash.tableColumnCumulative')}</th>
                </tr>
              </thead>
              <tbody>
                {model.weeks.map((w) => (
                  <tr key={w.weekStartISO} className="text-primary">
                    <td className="py-1 pr-3 whitespace-nowrap">{formatWeekLabel(w.weekStartISO, i18n.language)}</td>
                    <td className="py-1 pr-3 text-right whitespace-nowrap">
                      <Amount cents={w.inflowCents} size="sm" />
                    </td>
                    <td className="py-1 pr-3 text-right whitespace-nowrap">
                      <Amount cents={w.outflowCents} size="sm" />
                    </td>
                    <td className="py-1 text-right whitespace-nowrap">
                      <Amount cents={w.cumulativeNetCents} size="sm" tone={w.cumulativeNetCents < 0 ? 'danger' : 'default'} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      }}
    />
  );
};

export default NettoCashTile;
